import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {FlexRow, Label, Pill, PillVariant, TextArea, TextField} from '@forecast-it/design-system';
import {NovaAgentDraft} from '../state/useNovaAgentDraft';
import {PromptTarget} from '../state/useNovaPromptState';
import {NovaCategoryOption} from '../modals/CreateAgentModal';
import {MonospacePromptEditor} from './MonospacePromptEditor';

const Container = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	min-height: 0;
	gap: 24px;
`;

const CategoryContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

// Same unstyled wrapper CreateAgentModal's category picker uses: Pill renders no click handler
// and accepts no data-cy, so this is the minimal way to make a Pill clickable and individually
// selectable without adding visual styling of its own.
const ChipButton = styled.button`
	all: unset;
	display: inline-flex;
	cursor: pointer;
`;

// Tall enough to work with multi-paragraph prompts without immediately needing to scroll.
const EDITOR_HEIGHT = 220;

interface Props {
	draft: NovaAgentDraft;
	categoryOptions: NovaCategoryOption[];
}

// The System and User prompt template editors plus the category picker, all bound to the shared
// working draft. None of these save on their own — choosing a category only calls
// `draft.setCategoryId`, which marks the draft dirty; the header Save (⌘S / Save button) is the
// single point where the draft, including the category, is committed as a new version. The token
// picker is added by a later task.
export function NovaPromptTab({draft, categoryOptions}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	const {values, setValue, setActiveTarget, activeTarget} = draft.promptState;

	const categoryLabel = formatMessage({id: 'nova.prompt.categoryLabel', defaultMessage: 'Category'});
	const insertTargetHint = formatMessage({id: 'nova.prompt.insertTargetHint', defaultMessage: 'Variables insert here'});

	return (
		<Container data-cy="nova-prompt-tab" data-testid="nova-prompt-tab">
			<TextField
				label={formatMessage({id: 'nova.prompt.titleLabel', defaultMessage: 'Title'})}
				data-cy="nova-prompt-title"
				value={draft.title}
				onValueChange={(value: string) => draft.setTitle(value)}
				size={TextField.SIZE.LARGE}
				width={TextField.WIDTH.FLEX}
			/>
			<TextArea
				label={formatMessage({id: 'nova.prompt.descriptionLabel', defaultMessage: 'Description'})}
				data-cy="nova-prompt-description"
				value={draft.description}
				onChange={(value: string) => draft.setDescription(value)}
				onBlur={() => undefined}
				isOptional={false}
				height={80}
				width={TextArea.WIDTH.FLEX}
			/>
			<CategoryContainer data-cy="nova-prompt-category" role="group" aria-label={categoryLabel}>
				<Label>{categoryLabel}</Label>
				<FlexRow wrap gap={'s'}>
					{categoryOptions.map(category => {
						const isSelected = draft.categoryId === category.categoryId;
						return (
							<ChipButton
								key={category.categoryId}
								type="button"
								data-cy={`nova-prompt-category-${category.categoryId}`}
								aria-pressed={isSelected}
								onClick={() => draft.setCategoryId(category.categoryId)}
							>
								<Pill variant={isSelected ? PillVariant.HIGHLIGHT : PillVariant.NEUTRAL}>{category.name}</Pill>
							</ChipButton>
						);
					})}
				</FlexRow>
			</CategoryContainer>
			<MonospacePromptEditor
				label={formatMessage({id: 'nova.prompt.systemLabel', defaultMessage: 'System prompt'})}
				dataCy="nova-system-prompt"
				target={PromptTarget.System}
				value={values.systemPrompt}
				height={EDITOR_HEIGHT}
				onChange={(value: string) => setValue(PromptTarget.System, value)}
				onActivate={setActiveTarget}
				caption={activeTarget === PromptTarget.System ? insertTargetHint : undefined}
			/>
			<MonospacePromptEditor
				label={formatMessage({id: 'nova.prompt.userLabel', defaultMessage: 'User prompt template'})}
				dataCy="nova-user-prompt"
				target={PromptTarget.User}
				value={values.userPromptTemplate}
				height={EDITOR_HEIGHT}
				onChange={(value: string) => setValue(PromptTarget.User, value)}
				onActivate={setActiveTarget}
				caption={activeTarget === PromptTarget.User ? insertTargetHint : undefined}
			/>
		</Container>
	);
}
